// How online orders reach the tablets of a branch, as the API decides it
// (camel-step-ecommerce-api: OrderController.sendOrderToMerchantDevices + checkPendingOrders).
//
//   1. Table orders on a floor that has a tablet → that floor's tablet.
//   2. Deliverect orders from a virtual location (brand) a tablet claims → that tablet.
//   3. Aggregator orders (Deliverect / Blend) on a channel a tablet claims → that tablet.
//      A claim only counts while the tablet is active and has online orders enabled. A brand
//      claim beats a channel claim on the same order.
//   4. Everything else → the branch's primary tablet, or every online-orders tablet if none.
//
// Kept in one place so the summary card, the grouped table and the device modal all describe
// the same rules.

export const branchIdOf = (device) => device?.BranchId ?? device?.Branch?.id ?? null;

export const channelsOf = (device) => (Array.isArray(device?.routedChannels) ? device.routedChannels : []);

// [{ id, label, externalLocationId }] — the Deliverect virtual locations this tablet claims.
export const virtualLocationsOf = (device) =>
  (Array.isArray(device?.routedVirtualLocations) ? device.routedVirtualLocations : []);

// Channels and brands as one chip list ({ id, name }); brand ids are prefixed so they never
// collide with a channel id.
export const routingClaimsOf = (device) => [
  ...virtualLocationsOf(device).map((v) => ({ id: `vl:${v.id}`, name: `Location: ${v.label}` })),
  ...channelsOf(device),
];

export const deviceLabel = (device) => device?.name || `Device #${device?.id}`;

// Whether a tablet can take online orders at all. Mirrors the server's claim filter.
export const isReceiving = (device) => device?.isActive !== false && !!device?.onlineOrdersEnabled;

export const formatChannelNames = (channels, max = 3) => {
  const names = (channels || []).map((c) => c.name || c.id);
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max}`;
};

export const ROUTING_MODES = {
  channel: { label: "Channel / location routing", short: "Claims" },
  primary: { label: "Primary device", short: "Primary" },
  broadcast: { label: "All tablets", short: "All" },
};

export const summarizeBranch = (branchId, branchName, devices) => {
  const receiving = devices.filter(isReceiving);
  const primary = devices.find((d) => d.isPrimaryDevice) || null;
  const claims = devices
    .filter((d) => routingClaimsOf(d).length > 0)
    .map((d) => ({ device: d, channels: routingClaimsOf(d), effective: isReceiving(d) }));
  const claimedIds = new Set(claims.filter((c) => c.effective).flatMap((c) => c.channels.map((ch) => ch.id)));

  const mode = claims.some((c) => c.effective) ? "channel" : primary ? "primary" : "broadcast";
  const fallbackText = primary ? `primary (${deviceLabel(primary)})` : `all ${receiving.length} tablet(s)`;

  const issues = [];
  claims
    .filter((c) => !c.effective)
    .forEach((c) => {
      const why = c.device.isActive === false ? "is marked inactive" : "has online orders off";
      issues.push(
        `${deviceLabel(c.device)} claims ${formatChannelNames(c.channels)} but ${why}, so those orders go to ${fallbackText}.`
      );
    });
  if (primary && !isReceiving(primary)) {
    issues.push(
      `Primary ${deviceLabel(primary)} ${primary.isActive === false ? "is marked inactive" : "has online orders off"}, so orders routed to it are not received.`
    );
  }
  const duplicateRisk = !primary && receiving.length > 1;
  if (duplicateRisk) {
    issues.push(
      mode === "channel"
        ? `No primary device: aggregator orders on unclaimed channels or virtual locations arrive on all ${receiving.length} tablets.`
        : `No primary device: every online order arrives on all ${receiving.length} tablets.`
    );
  }

  return {
    branchId,
    branchName,
    devices,
    total: devices.length,
    online: devices.filter((d) => d.isConnected).length,
    receiving,
    primary,
    claims,
    claimedCount: claimedIds.size,
    floorDevices: devices.filter((d) => d.floorId).length,
    mode,
    fallbackText,
    duplicateRisk,
    issues,
  };
};

// [{ branchId, branchName, ...summary }] sorted by branch name.
export const summarizeBranches = (devices) => {
  const groups = new Map();
  (devices || []).forEach((d) => {
    const id = branchIdOf(d);
    const key = id ?? "none";
    if (!groups.has(key)) groups.set(key, { id, name: d?.Branch?.name || "No branch", devices: [] });
    groups.get(key).devices.push(d);
  });
  return [...groups.values()]
    .map((g) => summarizeBranch(g.id, g.name, g.devices))
    .sort((a, b) => String(a.branchName).localeCompare(String(b.branchName)));
};
